import React, { useEffect, useState, useCallback } from 'react';
import ClientPortalLayout from './Layout';
import { clientPortalFetch } from './api';
import { formatCurrency, formatDate } from './Dashboard';
import { downloadStatement } from './portalDocs';
import { buildInvoiceHtml, buildReceiptHtml, openPrintWindow } from '../../utils/invoiceDocument';

const NAVY = '#0f1f45';

const DownloadIcon = () => (
  <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
  </svg>
);

function LedgerInner({ me }) {
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(true);
  const [groupWide, setGroupWide] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);

  const hasGroup = me?.client?.group_id != null;

  const load = useCallback((wide) => {
    setLoading(true);
    clientPortalFetch(`/ledger${wide ? '?groupWide=true' : ''}`).then(setLedger).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(groupWide); }, [groupWide, load]);

  const handleDownload = async (entry, key) => {
    if (!entry.doc_id) return;
    setDownloadingKey(key);
    try {
      if (entry.type === 'Invoice') {
        const inv = await clientPortalFetch(`/invoices/${entry.doc_id}`);
        openPrintWindow(buildInvoiceHtml(inv));
      } else if (entry.type === 'Receipt') {
        const rec = await clientPortalFetch(`/receipts/${entry.doc_id}`);
        openPrintWindow(buildReceiptHtml({ ...rec, show_receipt_ledger: false }, { ...rec, amount: rec.received_amount }));
      }
    } catch (e) { alert(e.message); }
    setDownloadingKey(null);
  };

  const handleStatement = async () => {
    setDownloadingKey('statement');
    try { await downloadStatement(ledger, ledger.client?.client_name || me?.client?.client_name); } catch (e) { alert(e.message); }
    setDownloadingKey(null);
  };

  if (loading && !ledger) return <p style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</p>;
  if (!ledger) return null;

  return (
    <div>
      {hasGroup && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <ToggleBtn active={!groupWide} onClick={() => setGroupWide(false)}>This Company</ToggleBtn>
          <ToggleBtn active={groupWide} onClick={() => setGroupWide(true)}>All Group Companies</ToggleBtn>
        </div>
      )}

      <div style={{
        background: NAVY, borderRadius: 14, padding: '20px 22px', color: '#fff', marginBottom: 20,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {ledger.mode === 'group' ? `Combined Balance · ${ledger.group_name || 'Group'}` : 'Closing Balance'}
          </div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4 }}>{formatCurrency(ledger.closing_balance)}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            {ledger.closing_balance > 0 ? 'Amount payable to firm' : ledger.closing_balance < 0 ? 'Advance / credit balance' : 'Fully settled'}
          </div>
          <button onClick={handleStatement} disabled={downloadingKey === 'statement'} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.12)',
            color: '#fff', borderRadius: 8, padding: '8px 14px', fontSize: 12.5, fontWeight: 600, cursor: downloadingKey === 'statement' ? 'not-allowed' : 'pointer',
          }}>
            <DownloadIcon /> Download Statement
          </button>
        </div>
      </div>

      {ledger.mode === 'group' && ledger.companies?.length > 0 && (
        <div className="cp-cards" style={{ display: 'grid', gridTemplateColumns: `repeat(${ledger.companies.length}, 1fr)`, gap: 12, marginBottom: 20 }}>
          {ledger.companies.map((c) => (
            <div key={c.client_id} style={{ background: '#fff', borderRadius: 12, padding: '14px 16px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 4 }}>{c.client_name}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: NAVY }}>{formatCurrency(c.closing_balance)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="cp-ledger-table" style={{ background: '#fff', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead>
              <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                <Th>Date</Th><Th>Type</Th><Th>Reference</Th>
                {ledger.mode === 'group' && <Th>Company</Th>}
                <Th align="right">Debit</Th><Th align="right">Credit</Th><Th align="right">Balance</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {ledger.entries.map((e, i) => {
                const key = `${e.type}-${e.doc_id}-${i}`;
                const canDownload = e.doc_id && (e.type === 'Invoice' || e.type === 'Receipt');
                return (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <Td>{formatDate(e.date)}</Td>
                  <Td>{e.type}</Td>
                  <Td>{e.reference}</Td>
                  {ledger.mode === 'group' && <Td>{e.company_name}</Td>}
                  <Td align="right">{e.debit ? formatCurrency(e.debit) : '—'}</Td>
                  <Td align="right">{e.credit ? formatCurrency(e.credit) : '—'}</Td>
                  <Td align="right" bold>{formatCurrency(e.balance)}</Td>
                  <Td align="right">
                    {canDownload && (
                      <button onClick={() => handleDownload(e, key)} disabled={downloadingKey === key} title={`Download ${e.type}`}
                        style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: '#eff6ff', color: '#2563eb', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: downloadingKey === key ? 'not-allowed' : 'pointer' }}>
                        <DownloadIcon />
                      </button>
                    )}
                  </Td>
                </tr>
                );
              })}
              {ledger.entries.length === 0 && (
                <tr><td colSpan={ledger.mode === 'group' ? 8 : 7} style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No ledger entries yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .cp-cards { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

const ToggleBtn = ({ active, onClick, children }) => (
  <button onClick={onClick} style={{
    border: '1px solid #e5e7eb', borderRadius: 999, padding: '7px 16px', fontSize: 12.5, fontWeight: 500,
    cursor: 'pointer', background: active ? NAVY : '#fff', color: active ? '#fff' : '#374151',
  }}>
    {children}
  </button>
);

const Th = ({ children, align }) => (
  <th style={{ padding: '10px 16px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', textAlign: align || 'left' }}>{children}</th>
);
const Td = ({ children, align, bold }) => (
  <td style={{ padding: '10px 16px', fontSize: 13, color: bold ? NAVY : '#374151', fontWeight: bold ? 700 : 400, textAlign: align || 'left', whiteSpace: 'nowrap' }}>{children}</td>
);

function Ledger() {
  return <ClientPortalLayout title="Ledger"><LedgerInner /></ClientPortalLayout>;
}

export default Ledger;
